import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { APIURL } from '../api/config';
import {
  WindowResetAlert,
  WindowResetActionPayload,
  WindowResetRequestedPayload,
  getWindowResetAlertKey,
  getWindowResetLocalStorageKey,
} from '../types/windowReset.types';
import { playNotificationSound } from '../utils/playNotificationSound';

// ─── localStorage helpers ────────────────────────────────────────────────────

function lsPersist(slotId: string, payload: WindowResetAlert): void {
  try {
    localStorage.setItem(
      getWindowResetLocalStorageKey(slotId, payload.candidateId),
      JSON.stringify(payload)
    );
  } catch (err) {
    console.warn('[AssessmentSlotHub] localStorage.setItem failed:', err);
  }
}

function lsRemove(slotId: string, candidateId: string): void {
  try {
    localStorage.removeItem(getWindowResetLocalStorageKey(slotId, candidateId));
  } catch (err) {
    console.warn('[AssessmentSlotHub] localStorage.removeItem failed:', err);
  }
}

/**
 * Reads all pending reset-request entries for a given slot from localStorage.
 * Keys are formatted as windowReset_{slotId}_{candidateId}.
 */
function lsRestoreForSlot(slotId: string): Map<string, WindowResetAlert> {
  const result = new Map<string, WindowResetAlert>();
  try {
    const prefix = `windowReset_${slotId}_`;
    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i);
      if (!lsKey?.startsWith(prefix)) continue;
      const raw = localStorage.getItem(lsKey);
      if (!raw) continue;
      const alert: WindowResetAlert = JSON.parse(raw);
      result.set(getWindowResetAlertKey(alert.candidateId, alert.slotId), alert);
    }
  } catch (err) {
    console.warn('[AssessmentSlotHub] localStorage restore failed:', err);
  }
  return result;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Admin-side SignalR hook for a single assessment slot.
 *
 * Responsibilities:
 *  - On mount: restore pending alerts from localStorage (survives page refresh)
 *  - WindowResetRequested  → add/update alert in state + persist to localStorage + play sound
 *  - WindowResetGranted    → remove alert from state + localStorage on ALL connected PCs
 *  - WindowResetDismissed  → same as above
 *  - Exposes broadcast helpers so the UI can trigger cross-PC cleanup
 */
export function useSlotWindowResetAlerts(slotId: string | undefined) {
  const [activeResetAlerts, setActiveResetAlerts] = useState<Map<string, WindowResetAlert>>(
    new Map()
  );
  const soundPlayedKeysRef = useRef<Set<string>>(new Set());
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  // ── Restore persisted alerts from localStorage on mount / slotId change ──
  useEffect(() => {
    if (!slotId) return;
    const stored = lsRestoreForSlot(slotId);
    if (stored.size > 0) {
      setActiveResetAlerts(stored);
    }
  }, [slotId]);

  // ── SignalR connection ───────────────────────────────────────────────────
  useEffect(() => {
    if (!slotId) return;

    const hubBaseUrl = APIURL.replace(/\/api$/, '');
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubBaseUrl}/hubs/assessment`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // ── WindowResetRequested ──
    const handleWindowResetRequested = (payload: WindowResetRequestedPayload) => {
      const key = getWindowResetAlertKey(payload.candidateId, payload.slotId);

      const alert: WindowResetAlert = {
        candidateId: payload.candidateId,
        slotId: payload.slotId,
        candidateName: payload.candidateName,
        assessmentId: payload.assessmentId,
        reason: payload.reason,
        requestedAt: payload.requestedAt,
      };

      // Persist so other PCs (and page refreshes) can pick it up
      lsPersist(payload.slotId, alert);

      setActiveResetAlerts((prev) => {
        const next = new Map(prev);
        next.set(key, alert);
        return next;
      });

      // Play sound only once per unique alert (suppresses repeat requests)
      if (!soundPlayedKeysRef.current.has(key)) {
        playNotificationSound();
        soundPlayedKeysRef.current.add(key);
      }
    };

    // ── WindowResetGranted ── (broadcast by whoever clicked Reset Window)
    const handleWindowResetGranted = (payload: WindowResetActionPayload) => {
      clearAlert(payload.candidateId, payload.slotId);
    };

    // ── WindowResetDismissed ── (broadcast by whoever clicked Dismiss)
    const handleWindowResetDismissed = (payload: WindowResetActionPayload) => {
      clearAlert(payload.candidateId, payload.slotId);
    };

    function clearAlert(candidateId: string, alertSlotId: string) {
      const key = getWindowResetAlertKey(candidateId, alertSlotId);
      lsRemove(alertSlotId, candidateId);
      setActiveResetAlerts((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      soundPlayedKeysRef.current.delete(key);
    }

    connection.on('WindowResetRequested', handleWindowResetRequested);
    connection.on('WindowResetGranted', handleWindowResetGranted);
    connection.on('WindowResetDismissed', handleWindowResetDismissed);

    const joinSlot = () =>
      connection
        .invoke('JoinAdminAssessmentSlot', { assessmentSlotId: slotId })
        .catch((err) =>
          console.error('[AssessmentSlotHub] JoinAdminAssessmentSlot failed:', err)
        );

    connection
      .start()
      .then(() => {
        joinSlot();
      })
      .catch((err) => {
        console.error('[AssessmentSlotHub] Connection failed:', err);
      });

    connection.onreconnected(() => joinSlot());

    connectionRef.current = connection;

    return () => {
      connection.off('WindowResetRequested', handleWindowResetRequested);
      connection.off('WindowResetGranted', handleWindowResetGranted);
      connection.off('WindowResetDismissed', handleWindowResetDismissed);
      connection.stop().catch(() => undefined);
      connectionRef.current = null;
    };
  }, [slotId]);

  // ── Broadcast helpers (called by UI after user action) ───────────────────

  /**
   * Called after a successful resetWindow API call.
   * Broadcasts WindowResetGranted to the entire slot group — every PC removes
   * the alert from its state and localStorage simultaneously.
   * Falls back to local-only cleanup if the hub invoke fails.
   */
  const broadcastWindowResetGranted = useCallback(
    async (candidateId: string, alertSlotId: string) => {
      const conn = connectionRef.current;
      if (conn?.state === signalR.HubConnectionState.Connected) {
        try {
          await conn.invoke('BroadcastWindowResetGranted', {
            candidateId,
            slotId: alertSlotId,
          });
          // State + localStorage cleanup is handled by the incoming WindowResetGranted event
          return;
        } catch (err) {
          console.error('[AssessmentSlotHub] BroadcastWindowResetGranted failed:', err);
        }
      }
      // Fallback: clean up locally if not connected
      lsRemove(alertSlotId, candidateId);
      setActiveResetAlerts((prev) => {
        const next = new Map(prev);
        next.delete(getWindowResetAlertKey(candidateId, alertSlotId));
        return next;
      });
      soundPlayedKeysRef.current.delete(getWindowResetAlertKey(candidateId, alertSlotId));
    },
    []
  );

  /**
   * Called when the recruiter dismisses an alert without resetting the window.
   * Broadcasts WindowResetDismissed so all PCs drop the alert.
   * Falls back to local-only cleanup if not connected.
   */
  const broadcastWindowResetDismissed = useCallback(
    async (candidateId: string, alertSlotId: string) => {
      const conn = connectionRef.current;
      if (conn?.state === signalR.HubConnectionState.Connected) {
        try {
          await conn.invoke('BroadcastWindowResetDismissed', {
            candidateId,
            slotId: alertSlotId,
          });
          return;
        } catch (err) {
          console.error('[AssessmentSlotHub] BroadcastWindowResetDismissed failed:', err);
        }
      }
      // Fallback
      lsRemove(alertSlotId, candidateId);
      setActiveResetAlerts((prev) => {
        const next = new Map(prev);
        next.delete(getWindowResetAlertKey(candidateId, alertSlotId));
        return next;
      });
      soundPlayedKeysRef.current.delete(getWindowResetAlertKey(candidateId, alertSlotId));
    },
    []
  );

  const getAlertForCandidate = useCallback(
    (candidateId: string): WindowResetAlert | undefined => {
      if (!slotId) return undefined;
      return activeResetAlerts.get(getWindowResetAlertKey(candidateId, slotId));
    },
    [activeResetAlerts, slotId]
  );

  return {
    activeResetAlerts,
    getAlertForCandidate,
    broadcastWindowResetGranted,
    broadcastWindowResetDismissed,
  };
}
